import React, { useCallback, useMemo, useState } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import Dataset from "datasets/types/interfaces/dataset";
import { AlertCircle, UserRoundPlus } from "lucide-react";
import useSWR from "swr";
import { Button } from "~/components/ui/button";
import useNextGenHeader from "~/contexts/nextgen-header";
import useUser from "~/contexts/user";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { useFetcher } from "~/hooks/useFetcher";
import { buildEntityBreadcrumbs } from "~/pages/manifold-nextgen/breadcrumbs";
import { McpAppsPaneProvider } from "~/pages/manifold-nextgen/components/conversations/useMcpAppsPane";
import { ShareModal } from "~/pages/manifold-nextgen/components/share/ShareModal";
import { useProjectNeedsSetup } from "~/pages/manifold-nextgen/hooks/useProjectNeedsSetup";
import { useProjectShare } from "~/pages/manifold-nextgen/hooks/useProjectShare";
import { SessionContextPanelProvider } from "~/pages/manifold-nextgen/hooks/useSessionContextPanel";
import { nextgenProjectPath, nextgenProjectsListPath } from "~/pages/manifold-nextgen/paths";
import { Agent, agentPath } from "~/services/agents";
import {
  Project,
  ProjectAccessLevel,
  ProjectConversation,
  projectAgentsPath,
  projectConversationsPath,
  projectDatasetsPath,
  projectPath,
} from "~/services/projects";
import { apiClient } from "~/utils/apiClient";
import { ProjectSidebar } from "./ProjectSidebar";
import { SessionContextSidebar } from "./SessionContextSidebar";

const { VITE_CATALOG_API } = import.meta.env;
const CATALOG_DATASETS_URL = `${VITE_CATALOG_API}/datasets?offset=0&limit=1000&sort_by=title&sort_order=asc`;

export interface ProjectOutletContext {
  projectId: string;
  project: Project;
  // Whether the caller owns (can_manage) this project. Gates owner-only controls.
  isOwner: boolean;
  sessions: ProjectConversation[] | undefined;
  attachedAgents: Agent[] | undefined;
  attachedAgentsWithDetails: Agent[] | undefined;
  attachedDatasets: Dataset[] | undefined;
}

export const ProjectLayout = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const currentUser = useUser();
  const [projectShareOpen, setProjectShareOpen] = useState(false);

  const {
    collaborators,
    isLoading: collaboratorsLoading,
    collaboratorsError,
    handleShare,
    accessLevels,
    defaultAccessLevel,
  } = useProjectShare(projectId ?? "");

  const {
    data: project,
    error,
    isLoading,
  } = useFetcher<Project>(projectId ? projectPath(projectId) : null);

  const { data: attachedAgents } = useFetcher<Agent[]>(
    projectId ? projectAgentsPath(projectId) : null
  );

  const agentIds = attachedAgents?.map((a) => a.id);
  // Fetches full agent details (including starter_prompts) individually because
  // GET /projects/:id/agents returns a lightweight shape without that field.
  // Ideally starter_prompts would be included in that endpoint to avoid N requests.
  const { data: agentsWithDetails } = useSWR<Agent[]>(
    agentIds?.length ? ["project-agents-full", ...[...agentIds].sort()] : null,
    ([, ...ids]: [string, ...string[]]) =>
      Promise.all(ids.map((id) => apiClient<Agent>(agentPath(id))))
  );

  const { data: attachedDatasetIds } = useFetcher<string[]>(
    projectId ? projectDatasetsPath(projectId) : null
  );

  const { data: catalogDatasets } = useFetcher<Dataset[]>(CATALOG_DATASETS_URL);

  const { data: sessionsList } = useFetcher<{ items: ProjectConversation[] }>(
    projectId ? projectConversationsPath(projectId) : null
  );
  const sessions = sessionsList?.items;

  const attachedDatasets = useMemo<Dataset[] | undefined>(() => {
    if (attachedDatasetIds === undefined || catalogDatasets === undefined) {
      return undefined;
    }
    const byId = new Map(catalogDatasets.map((d) => [d.id, d]));
    return attachedDatasetIds.map((id) => byId.get(id)).filter((d): d is Dataset => Boolean(d));
  }, [attachedDatasetIds, catalogDatasets]);

  const conversationId = location.pathname.match(/\/c\/([^/]+)/)?.[1];
  const isOwner = project?.access_level === ProjectAccessLevel.OWNER;

  // The chats route renders ProjectSetupWizard instead of the new-session
  // picker until the project has at least one dataset and one enabled agent
  // (for an owner — mirrors ProjectIndex's own "setup" mode condition).
  const needsSetup = useProjectNeedsSetup(attachedDatasets, attachedAgents, isOwner);

  // The session context sidebar only shows on the new-session picker and
  // individual session pages — not on Overview, Settings, or the setup
  // wizard, which use the full content area for their own purposes.
  const isChatsRoute = /\/chats$/.test(location.pathname);
  const showContextSidebar = (isChatsRoute && !needsSetup) || !!conversationId;
  const currentSession = conversationId
    ? sessions?.find((s) => s.id === conversationId)
    : undefined;

  const conversationLabel = currentSession?.display_name;

  useDocumentTitle(conversationLabel ?? project?.name ?? "");

  // Always show "All → Project Name". When inside a session, add the session name
  // as a trailing crumb so users know which conversation they're in.
  const breadcrumbs = useMemo(
    () =>
      buildEntityBreadcrumbs({
        rootHref: nextgenProjectsListPath(),
        entityName: project?.name ?? "Loading…",
        entityHref: conversationId && projectId ? nextgenProjectPath(projectId) : undefined,
        sectionLabel: conversationId ? (conversationLabel ?? "Loading…") : undefined,
      }),
    [project?.name, projectId, conversationId, conversationLabel]
  );

  const handleOpenProjectShare = useCallback(() => setProjectShareOpen(true), []);

  // Only surface header actions once the project has loaded: the modals they
  // open are rendered in the success-path return below, so controls shown
  // during load or on the error state would click into nothing.
  const headerActions = useMemo<[React.ReactNode?]>(() => {
    if (!project) {
      return [];
    }
    // Share slot represents the project (the entity the header is scoped to):
    // only owners can share the project itself. Session sharing lives inline in
    // the composer. Project-level settings (leave / delete) live on the Settings
    // page reached from the sidebar.
    const shareNode = isOwner ? (
      <Button key="share-project" size="sm" className="text-sm" onClick={handleOpenProjectShare}>
        <UserRoundPlus className="size-4" />
        Share
      </Button>
    ) : undefined;

    return [shareNode];
  }, [project, isOwner, handleOpenProjectShare]);

  useNextGenHeader({ breadcrumbs, actions: headerActions });

  if (!projectId) {
    return (
      <div role="alert" className="p-6 text-sm text-destructive">
        Missing project id.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full overflow-hidden">
        <div
          className="w-56 shrink-0 border-r border-border"
          role="status"
          aria-label="Loading project"
        />
        <div className="flex-1" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div
        role="alert"
        className="flex h-full flex-col items-center justify-center gap-3 text-center text-destructive"
      >
        <AlertCircle className="size-8" aria-hidden="true" />
        <p className="text-sm font-medium">Failed to load project</p>
      </div>
    );
  }

  const outletContext: ProjectOutletContext = {
    projectId,
    project,
    isOwner,
    sessions,
    attachedAgents,
    attachedAgentsWithDetails: agentsWithDetails,
    attachedDatasets,
  };

  return (
    <>
      <div className="flex h-full overflow-hidden">
        <ProjectSidebar projectId={projectId} sessions={sessions} />
        <SessionContextPanelProvider>
          <McpAppsPaneProvider>
            <div className="flex flex-1 flex-col overflow-hidden">
              <Outlet context={outletContext} />
            </div>
            {showContextSidebar && (
              <SessionContextSidebar
                projectId={projectId}
                isOwner={outletContext.isOwner}
                attachedDatasets={attachedDatasets}
                attachedAgents={attachedAgents}
              />
            )}
          </McpAppsPaneProvider>
        </SessionContextPanelProvider>
      </div>
      <ShareModal
        open={projectShareOpen}
        onOpenChange={setProjectShareOpen}
        resourceLabel="project"
        resourceName={project.name}
        collaborators={collaborators}
        collaboratorsLoading={collaboratorsLoading}
        collaboratorsError={collaboratorsError}
        accessLevels={accessLevels}
        defaultAccessLevel={defaultAccessLevel}
        onShare={handleShare}
        currentUserEmail={currentUser?.email}
      />
    </>
  );
};
